import { useEffect, useState } from "react";
import { type ImageCache, imageCache } from "./ImageCache";
import type { ImageCacheSnapshot } from "./types";

export interface UseImageSourceResult {
  /**
   * 표시 가능한 URL.
   *
   * - 캐시가 사전 디코드까지 완료한 경우에만 채워진다 (`status === "ready"`).
   * - 즉, 이 값이 truthy 인 시점부터 `<img>` 를 렌더해도 흰 깜박임 없이 표시된다.
   *   (브라우저가 동일 URL 의 디코드 결과를 hot 한 비트맵으로 보유 중이기 때문)
   */
  src: string | null;
  /** 캐시가 변환/디코드 작업 중. */
  isLoading: boolean;
  /** 변환/디코드 실패. */
  error: Error | null;
}

export interface UseImageSourceOptions {
  /**
   * false 인 동안에는 캐시를 acquire 하지 않는다.
   *
   * 뷰포트 밖이거나 lazy-loading 대기 중인 이미지에 대해 변환·디코드 비용을 회피.
   * 기본값 true.
   */
  enabled?: boolean;
  /**
   * 이 소스를 HEIC 로 다뤄 변환 경로로 보낼지 명시한다.
   *
   * 미지정 시 URL 확장자로 추정한다. blob URL HEIC 미리보기는 `true` 명시가 필요하다.
   */
  isHeic?: boolean;
  /**
   * 사용할 캐시 인스턴스. 기본은 모듈 싱글톤.
   *
   * 테스트나 격리된 컨텍스트에서만 명시적으로 주입한다.
   */
  cache?: ImageCache;
}

/**
 * `state.trackedSrc` 는 가장 최근에 본 src 를 기억해 다음 render 에서 변경 감지에 사용.
 * `state.snapshot` 은 현재 src 에 대응하는 캐시 스냅샷.
 *
 * 두 값을 한 객체로 묶어 atomic 하게 갱신해야 src↔snapshot 정합성이 깨지지 않는다.
 */
interface HookState {
  trackedSrc: string;
  snapshot: ImageCacheSnapshot | null;
}

function buildInitialState(src: string, enabled: boolean, isHeic: boolean | undefined, cache: ImageCache): HookState {
  if (!enabled) return { trackedSrc: src, snapshot: null };

  const snap = cache.peek(src, { isHeic });
  // 캐시된 error 는 곧 이어지는 acquire 가 재시도 한다 (캐시 layer 의 retry 정책).
  // 따라서 첫 commit 부터 stale error 를 노출하지 않고 loading 으로 시작한다.
  return {
    trackedSrc: src,
    snapshot: snap?.status === "error" ? null : snap,
  };
}

/**
 * 이미지 캐시에서 표시용 URL 을 받아온다.
 *
 * 동작
 * - 캐시에 사전 디코드된 결과가 있으면 첫 commit 부터 `src` 를 즉시 노출 (skeleton 없음).
 * - 없으면 `acquire` 로 캐시에 등록하고 `ready` 상태를 기다린다.
 * - 마운트 동안 refCount 를 보유하므로 다른 컴포넌트의 LRU evict 에 휩쓸리지 않는다.
 * - src 가 바뀌면 render 단계에서 즉시 stale snapshot 을 무효화 (이전 이미지가
 *   새 자리에 잠깐 보이는 사고 방지).
 *
 * 흰 깜박임 방지
 * - 캐시는 off-DOM `<img>` 로 비트맵을 hot 하게 잡고 있으므로,
 *   이 hook 이 `src` 를 발행한 시점에 React `<img>` 가 mount 되어 같은 URL 을 요청하면
 *   브라우저는 디코드된 데이터를 즉시 사용한다.
 * - 추가로 표시 컴포넌트가 `<img>.decode()` 를 await 후 페이드인하면 흰 프레임이 완전히 사라진다.
 */
export function useImageSource(src: string, options?: UseImageSourceOptions): UseImageSourceResult {
  const { enabled = true, isHeic, cache = imageCache } = options ?? {};

  const [state, setState] = useState<HookState>(() => buildInitialState(src, enabled, isHeic, cache));

  // src 변경 시 render 단계에서 동기 갱신.
  // useEffect 로 처리하면 한 commit 동안 옛 snapshot 이 새 src 자리에 표시될 수 있다.
  if (state.trackedSrc !== src) {
    setState(buildInitialState(src, enabled, isHeic, cache));
  }

  useEffect(() => {
    if (!enabled) return;

    const release = cache.acquire(
      src,
      (snap) => {
        // 콜백이 늦게 도착했을 때 다른 src 의 state 를 덮어쓰지 않도록 가드
        setState((prev) => (prev.trackedSrc === src ? { ...prev, snapshot: snap } : prev));
      },
      { isHeic },
    );
    return release;
  }, [src, enabled, isHeic, cache]);

  if (!enabled) {
    return { src: null, isLoading: false, error: null };
  }

  const { snapshot } = state;

  if (!snapshot) {
    return { src: null, isLoading: true, error: null };
  }

  if (snapshot.status === "error") {
    return {
      src: null,
      isLoading: false,
      error: snapshot.error ?? new Error("Image load failed"),
    };
  }

  if (snapshot.status === "ready") {
    return { src: snapshot.url ?? null, isLoading: false, error: null };
  }

  return { src: null, isLoading: true, error: null };
}
